import {customElement, property, query, state} from "lit/decorators.js";
import {html, LitElement, type PropertyValues, type TemplateResult} from "lit";
import Styles from "./livepoll.lit.scss";
import {Dictionary} from "../../page/elements/dictionary";
import {resolve} from "../../container";
import {LivepollService, type OpinionDistribution} from "./livepollService";
import {LocalStorage} from "../../common/clientStorage";
import {Tracking} from "../../tracking/tracking";
import {TrackingEventName, TrackingEventType} from "../../tracking/types";
import {DraggableSlider} from "./draggableSlider";
import {classMap} from "lit/directives/class-map.js";

const VOTED_POLL_IDS_KEY = "votedPollIds";


@customElement("eop-livepoll")
export class Livepoll extends LitElement {

    public static readonly styles = Styles;

    @property({attribute: "poll-id"})
    public pollId: string;
    @property({attribute: "groups", type: Number})
    public groups: number;

    @query(".slider-track")
    private sliderTrack: HTMLElement;
    @query(".slider-handle")
    private sliderHandle: HTMLElement;

    @state()
    private currentPosition: number = 50;

    @state()
    private opinionSubmitted: boolean = false;

    @state()
    private resultsRecieved: boolean = false;

    @property()
    private results: Array<OpinionDistribution>;

    private dictionary: Dictionary;

    private slider: DraggableSlider;

    public constructor(
        private localStorage: LocalStorage = resolve(LocalStorage),
        private livepollService: LivepollService = resolve(LivepollService),
        private tracking: Tracking = resolve(Tracking)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
        const previousVote = this.localStorage.fetchFromPath<{ value: number }>([VOTED_POLL_IDS_KEY, this.pollId]);
        const hasVotedBefore = Boolean(previousVote);
        if (hasVotedBefore) {
            this.currentPosition = previousVote!.value;
            this.getResults();
            this.opinionSubmitted = true;
        } else {
            this.slider = new DraggableSlider(this.sliderHandle, this.sliderTrack.offsetWidth, this.startDragging, this.updateCurrentPosition, this.submitVote);
        }
    }

    private startDragging = (): void => {
        this.sliderHandle.classList.add("dragging");
    };

    private updateCurrentPosition = (position: number): void => {
        this.currentPosition = position;
    };

    public submitVote = async (): Promise<void> => {
        this.opinionSubmitted = true;
        this.slider.destroy();
        this.sliderHandle.classList.remove("dragging");
        this.sliderHandle.classList.add("results");

        await this.livepollService.submitOpinion(
            this.pollId,
            {opinion: Math.round(this.currentPosition)}
        );
        this.tracking.emitTrackingEvent({
            name: TrackingEventName.BUTTON_INTERACT,
            data: {
                text: this.pollId,
                contentId: `livepoll.${this.pollId}`,
                type: TrackingEventType.CLICK
            }
        });
        this.getResults();
        this.localStorage.saveInPath(VOTED_POLL_IDS_KEY, [this.pollId], {value: this.currentPosition});
    };

    private getResults(): void {
        this.livepollService.getResults(this.pollId, this.groups)
            .then(opinions => {
                this.results = opinions;
                this.resultsRecieved = true;
            });
    }

    public render(): TemplateResult {
        const sliderTrackClasses = {"with-results": this.resultsRecieved};
        const sliderHandleClasses = {"results": this.opinionSubmitted};

        return html`
            ${this.resultsRecieved ? this.renderResults() : this.renderCTA()}
            <div class="slider-track ${classMap(sliderTrackClasses)}">
                <div style="left: ${this.currentPosition}%" class="slider-handle-wrapper">
                    <div class="slider-handle ${classMap(sliderHandleClasses)}"
                         tabindex=${this.opinionSubmitted ? -1 : 0}
                         role="slider"
                         aria-label=${this.dictionary.getMessage("MSG_POLL_CTA")}
                         aria-valuemin="0"
                         aria-valuemax="100"
                         aria-valuenow=${Math.round(this.currentPosition)}>
                    </div>
                </div>
            </div>
        `;
    }

    private renderCTA(): TemplateResult {
        return html`
            <span>${this.dictionary.getMessage("MSG_POLL_CTA")}</span>`;
    }

    private renderResults(): TemplateResult {
        const gap = this.sliderTrack.offsetWidth / ((this.groups * 3) - 1);
        const groupSize = 100 / this.groups;
        const votedGroup = Math.min(Math.floor(this.currentPosition / groupSize) + 1, this.groups);
        const maxValue = Math.max(...this.results.map(distribution => distribution.percentage));

        const resultItems = this.results.map((distribution: OpinionDistribution): TemplateResult => {
                const resultSegmentClasses = {"voted": distribution.group === votedGroup};
                const value = distribution.percentage / maxValue * 100;
                return html`
                    <div class="result-segment ${classMap(resultSegmentClasses)}" style="height: ${value}%"></div>`;
            }
        );

        return html`
            <span>${this.dictionary.getMessage("MSG_POLL_THANKS")}</span>
            <div class="result-container" style="gap: ${gap}px">
                ${resultItems}
            </div>`;
    }
}